
import { useState } from 'react';
import { motion } from 'framer-motion';
import { Credit, actorCredits, producerCredits, writerCredits, filmographyStats } from '../../../mocks/filmography';
import { actorInfo } from '../../../mocks/portfolio';

type FilterType = 'All' | 'Film' | 'TV Series' | 'Short' | 'TV Movie';

const CreditCard = ({ credit, index }: { credit: Credit; index: number }) => {
  const typeColors: Record<string, string> = {
    'Film': 'bg-burgundy/20 text-burgundy border-burgundy/40',
    'TV Series': 'bg-amber-900/20 text-amber-400 border-amber-700/40',
    'Short': 'bg-stone-700/20 text-stone-300 border-stone-600/40',
    'TV Movie': 'bg-rose-900/20 text-rose-300 border-rose-700/40',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      className="group flex items-center gap-6 py-5 px-6 rounded-lg hover:bg-white/5 transition-all duration-300 border-b border-white/5 last:border-b-0"
    >
      {/* Year */}
      <div className="w-16 shrink-0 text-right">
        <span className="text-sm font-medium text-stone-500 group-hover:text-stone-400 transition-colors">
          {credit.year}
        </span>
      </div>

      {/* Type Badge */}
      <div className="w-24 shrink-0">
        <span className={`inline-block text-xs px-2.5 py-1 rounded-md border whitespace-nowrap ${typeColors[credit.type]}`}>
          {credit.type}
        </span>
      </div>

      {/* Title & Role */}
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-3 flex-wrap">
          <h3 className="text-base font-semibold text-white group-hover:text-burgundy transition-colors">
            {credit.title}
          </h3>
          {credit.episodes && (
            <span className="text-xs text-stone-500">{credit.episodes}</span>
          )}
          {credit.notes && (
            <span className="text-xs text-stone-500 italic">{credit.notes}</span>
          )}
        </div>
      </div>

      {/* Character */}
      <div className="shrink-0 text-right">
        <span className="text-sm text-stone-400 font-light italic">
          as <span className="text-stone-300 font-normal not-italic">{credit.role}</span>
        </span>
      </div>

      {/* Arrow */}
      <div className="w-5 h-5 flex items-center justify-center shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
        <i className="ri-arrow-right-s-line text-stone-500"></i>
      </div>
    </motion.div>
  );
};

const CreditSection = ({ title, icon, credits }: { title: string; icon: string; credits: Credit[] }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5 }}
    className="mb-16"
  >
    <div className="flex items-center gap-3 mb-6">
      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-burgundy/20">
        <i className={`${icon} text-burgundy text-sm`}></i>
      </div>
      <h2 className="font-serif text-2xl text-white font-medium">{title}</h2>
      <span className="text-xs text-stone-500 bg-white/5 px-2.5 py-1 rounded-full">{credits.length}</span>
    </div>
    <div className="bg-white/[0.02] rounded-xl border border-white/5">
      {credits.map((credit, index) => (
        <CreditCard key={credit.id} credit={credit} index={index} />
      ))}
    </div>
  </motion.div>
);

const FilmographyList = () => {
  const [activeFilter, setActiveFilter] = useState<FilterType>('All');

  const filters: FilterType[] = ['All', 'Film', 'TV Series', 'Short', 'TV Movie'];

  const filteredCredits = activeFilter === 'All'
    ? actorCredits
    : actorCredits.filter(c => c.type === activeFilter);

  const filterCounts: Record<FilterType, number> = {
    'All': actorCredits.length,
    'Film': actorCredits.filter(c => c.type === 'Film').length,
    'TV Series': actorCredits.filter(c => c.type === 'TV Series').length,
    'Short': actorCredits.filter(c => c.type === 'Short').length,
    'TV Movie': actorCredits.filter(c => c.type === 'TV Movie').length,
  };

  return (
    <section className="max-w-5xl mx-auto px-8 py-20">
      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-2 mb-12">
        {filters.map(filter => (
          <button
            key={filter}
            onClick={() => setActiveFilter(filter)}
            className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
              activeFilter === filter
                ? 'bg-burgundy text-white'
                : 'bg-white/5 text-stone-400 hover:bg-white/10 hover:text-white border border-white/10'
            }`}
          >
            {filter}
            <span className="ml-2 text-xs opacity-60">{filterCounts[filter]}</span>
          </button>
        ))}
      </div>

      {/* Actor Credits */}
      <CreditSection title="Actor" icon="ri-movie-2-line" credits={filteredCredits} />

      {/* Producer Credits */}
      {activeFilter === 'All' && (
        <CreditSection title="Producer" icon="ri-clapperboard-line" credits={producerCredits} />
      )}

      {/* Writer Credits */}
      {activeFilter === 'All' && (
        <CreditSection title="Writer" icon="ri-quill-pen-line" credits={writerCredits} />
      )}

      {/* IMDb Link */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="text-center mt-8"
      >
        <a
          href="https://www.imdb.com/name/nm7411796/"
          target="_blank"
          rel="nofollow noopener noreferrer"
          className="inline-flex items-center gap-2 px-8 py-3 rounded-full border-2 border-white/20 text-white text-sm font-medium hover:bg-white hover:text-black transition-all duration-300 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-film-line"></i>
          View Full Profile on IMDb
        </a>
      </motion.div>

      {/* Stats */}
      <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6">
        {[
          { label: 'Total Credits', value: filmographyStats.totalCredits.toString() },
          { label: 'Films', value: filmographyStats.filmCredits.toString() },
          { label: 'TV Series', value: filmographyStats.tvCredits.toString() },
          { label: 'Years Active', value: filmographyStats.yearsActive },
        ].map((stat, i) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1 }}
            className="text-center p-6 rounded-xl bg-white/[0.03] border border-white/5"
          >
            <div className="font-serif text-3xl text-white mb-1">{stat.value}</div>
            <div className="text-xs text-stone-500 uppercase tracking-widest">{stat.label}</div>
          </motion.div>
        ))}
      </div>

      {/* SAG-AFTRA Badge */}
      <div className="mt-8 text-center">
        <span className="inline-flex items-center gap-2 text-xs text-stone-500 uppercase tracking-widest">
          <i className="ri-shield-check-line w-4 h-4 flex items-center justify-center"></i>
          {filmographyStats.affiliation} Member
        </span>
      </div>
    </section>
  );
};

export default FilmographyList;
